import type { PaletteOptions } from '@mui/material';

/**
 * Farbpaletten des CRM (Dark Mode, Decision #248, 28.10.2026).
 *
 * `hellePalette` traegt Zeichen fuer Zeichen die Werte, die bis zum Dark Mode
 * direkt in `theme/index.tsx` standen — der Hell-Modus bleibt pixelgleich.
 * `dunklePalette` belegt dieselben Rollen fuer den dunklen Modus
 * (Recherche 28.10.2026 13:33, Kontraste nach WCAG gerechnet, Ziel 4,5:1).
 *
 * 🔥 ROLLEN, NICHT NAMEN: `text.secondary` ist die Ueberschrift (dunkel im
 * Hellen, hell im Dunkeln), `text.primary` der Fliesstext — die Rollen sind
 * historisch vertauscht (Design-Test 25.10.2026), die dunkle Palette folgt
 * der Rolle.
 *
 * Neue Farbe? Hier in BEIDE Paletten eintragen und im Screen-Code den Pfad
 * nennen (`sx={{ color: 'status.green.text' }}`) oder `tokenVar(...)` —
 * nie einen Hex-Wert (Design-Waechter, Art `farbe`).
 */
export const hellePalette = {
  mode: 'light',
  primary: {
    hover: '#EBF6FA',
    darker: '#212A48',
    dark: '#212A48',
    main: '#036A8B',
    contrastText: '#FFFFFF',
    light: '#E6F1F5',
    altLight: '#0A669E',
    altLighter: '#B9E8F1',
    metalic: '#CAD9F1',
    metalicLight: 'rgba(247, 247, 248, 0.88)',
    metalicDark: '#EBEFF1',
  },
  // Design-Test: Flaechen auf Grau/Weiss. `screen` ist der Seitengrund der
  // Dashboards, `card` der Zeilen-Hover der DataTable.
  background: {
    default: '#F5F5F7',
    paper: '#FFFFFF',
    screen: '#F5F5F7',
    card: '#FAFAFA',
    subtle: '#F5F5F7',
    // Dark Mode: Hover-Grund von Text-/Icon-Knoepfen und Tooltip-Grund. Im
    // Hellen die Werte, die vorher fest in den Overrides standen.
    hover: '#F5F5F7',
    tooltip: '#111113',
    // Stufe 2b: Navigation wieder Navy wie Original (`primary.darker`),
    // aktiver/gehoverter Eintrag als leichte Aufhellung (Original-Hover).
    // Abschluss Rollout (Alex 26.10.2026): Navy Variante C — gleich dunkel wie
    // #212A48 (Weiss 14,1:1, Gold 6,1:1), Blaustich etwa halbiert (S 37 -> 19 %).
    navigation: '#272B38',
    navigationAktiv: 'rgba(255, 255, 255, 0.08)',
  },
  secondary: {
    main: '#CFA460',
    light: '#E0C590',
    dark: '#B08840',
    contrastText: '#FFFFFF',
    hoverOnDark: 'rgba(255, 255, 255, 0.06)',
    sky: '#E4F0F5',
    skyLight: '#F1F8FF',
    normalGrey: 'rgba(153, 153, 153, 0.28)',
    darkGrey: '#3D4655',
    bluishGrey: '#E8EEF1',
  },
  success: {
    light: '#ECFDF3',
    main: '#ECFDF3',
    contrastText: '#027A48',
  },
  error: {
    light: '#FEF3F2',
    main: '#F04438',
    contrastText: '#B42318',
  },
  /**
   * Warn- und Info-Hinweisflaechen (Design-Paket 2b, Entscheid Alex
   * 11.09.2026). Drei Rollen je Familie, wie bei `error`/`success` gelebt:
   *   `flaeche`  Hintergrund der Box      `rahmen`  1px-Rand      `text`  Schrift
   *
   * `main`/`light`/`dark` stehen HIER AUSDRUECKLICH auf den MUI-Defaults:
   * rund 15 Bestandsstellen nutzen sie als Signal- bzw. Orange-Flaeche.
   */
  warning: {
    main: '#ED6C02',
    light: '#FF9800',
    dark: '#E65100',
    contrastText: '#FFFFFF',
    flaeche: '#FFFAEB',
    rahmen: '#FEDF89',
    text: '#B54708',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#FFFFFF',
    flaeche: '#F1F8FF',
    rahmen: '#B9E8F1',
    text: '#0A669E',
  },
  /**
   * 🔥 DESIGN-TEST: Rollen sind VERTAUSCHT belegt — `text.secondary` ist das
   * dunkle Headline-Grau (~650 Aufrufer als „dunkel"), `text.primary` der
   * Fliesstext. Korrekturrunde 2 (Alex 26.10.2026): Grau #5F5F64 (6,4:1),
   * hellgrau #6E6E73 (5,1:1), dunkel #111113 (18,9:1).
   */
  text: {
    primary: '#5F5F64',
    secondary: '#111113',
    muted: '#6E6E73',
    subtle: '#5F5F64',
    strong: '#111113',
  },
  grey: {
    50: '#F5F5F7',
    100: '#E5E5EA',
    200: '#C7C7CC',
    800: '#5F5F64',
    900: '#111113',
    A100: '#E5E5EA',
    A200: '#FAFAFA',
    A400: '#212A48',
    A700: '#212A48',
  },
  divider: '#E5E5EA',
  /**
   * Status-Farbpaare (DESIGN_SYSTEM.md „Status Tags"): `flaeche` Pill-Grund,
   * `text` Schrift, `punkt` Timeline-Punkt.
   */
  status: {
    green: { flaeche: '#E8F5E9', text: '#2E7D32', punkt: '#2E7D32' },
    blue: { flaeche: '#E3F2FD', text: '#1565C0', punkt: '#1565C0' },
    gold: { flaeche: '#FFF8E1', text: '#8D6E1F', punkt: '#D69D5D' },
    orange: { flaeche: '#FFF3E0', text: '#E65100', punkt: '#E65100' },
    red: { flaeche: '#FFEBEE', text: '#C62828', punkt: '#C62828' },
    gray: { flaeche: '#F5F5F5', text: '#616161', punkt: '#616161' },
    teal: { flaeche: '#E0F2F1', text: '#036A8B', punkt: '#036A8B' },
    purple: { flaeche: '#F3E5F5', text: '#7B1FA2', punkt: '#7B1FA2' },
  },
  /**
   * Chart-Farben (Design-Rollout 25.10.2026). Recharts braucht echte
   * Farbwerte: Aufrufer lesen `useTheme().palette.chart.<name>` — im Dark Mode
   * liefert `useTheme()` die dunkle Palette (CssVarsProvider mischt das aktive
   * Schema ein), ein statisch importiertes Theme NICHT.
   */
  chart: {
    petrol: '#00546F',
    gold: '#CFA460',
    goldHell: '#D69D5D',
    teal: '#036A8B',
    navy: '#212A48',
    blau: '#0A669E',
    blauHell: '#068EB9',
    gruen: '#2E7D32',
    gruenHell: '#4CAF50',
    gruenDunkel: '#1B5E20',
    orange: '#E65100',
    rot: '#D33F33',
    gelb: '#F8C80A',
    grau: '#9E9E9E',
    // Schiefergrau als SERIENFARBE, nicht mit `beschriftung` verwechseln.
    schiefer: '#475467',
    raster: '#E5E5EA',
    achse: '#6E6E73',
    beschriftung: '#5F5F64',
    flaeche: '#FFFFFF',
  },
  /** Schrift-/Linienfarben auf der Navy-Navigation (Stufe 2b) — in beiden Modi gleich. */
  navigation: {
    text: '#FFFFFF',
    gedimmt: 'rgba(255, 255, 255, 0.55)',
    aktiv: '#CFA460',
    rand: 'rgba(255, 255, 255, 0.06)',
    trenner: 'rgba(255, 255, 255, 0.15)',
    scroll: 'rgba(255, 255, 255, 0.2)',
  },
  /** Farbtoken der Board-Spalten — die DB speichert den NAMEN, nie den Wert. */
  boardSpalte: {
    grau: { akzent: '#98A2B3', flaeche: '#F2F4F7' },
    blau: { akzent: '#036A8B', flaeche: '#E6F1F5' },
    gruen: { akzent: '#027A48', flaeche: '#ECFDF3' },
    gelb: { akzent: '#B54708', flaeche: '#FFFAEB' },
    rot: { akzent: '#B42318', flaeche: '#FEF3F2' },
    lila: { akzent: '#6941C6', flaeche: '#F4F3FF' },
  },
  /**
   * Kalender (Dark Mode): Raster- und Zustandsflaechen der Wochenansicht,
   * vorher als rgba in `CalendarWeekView`. Werte im Hellen unveraendert.
   */
  kalender: {
    stundenlinie: 'rgba(0,0,0,0.06)',
    stundenlinieFein: 'rgba(0,0,0,0.045)',
    halbstunde: 'rgba(0,0,0,0.025)',
    kopfRand: 'rgba(0,0,0,0.04)',
    gesperrt: 'rgba(0,0,0,0.025)',
    heute: 'rgba(3,106,139,0.02)',
    heuteHover: 'rgba(3,106,139,0.06)',
    slotHover: 'rgba(0,0,0,0.02)',
    auswahl: 'rgba(3,106,139,0.10)',
    auswahlAktiv: 'rgba(3,106,139,0.18)',
    auswahlRahmen: 'rgba(3,106,139,0.12)',
    abdeckung: 'rgba(255,255,255,0.85)',
    bestaetigt: 'rgba(46,125,50,0.9)',
    bestaetigtHover: 'rgba(46,125,50,1)',
    neutral: 'rgba(120,120,120,0.7)',
    farbRand: 'rgba(0,0,0,0.12)',
  },
  /**
   * Feste Icon-Farben der SVGs aus `assets/icons` (Dark Mode). Die Dateien
   * nennen `var(--mui-palette-icon-<name>, <Hellwert>)` — der Rueckfallwert
   * ist der alte Wert, damit das Icon ohne Theme (Tests, Strecke) unveraendert
   * bleibt.
   */
  icon: {
    teal: '#036A8B',
    tealHell: '#0391BF',
    petrol: '#00546F',
    rot: '#B42318',
    grau: '#667085',
    schiefer: '#475467',
    schieferBlau: '#4A5966',
  },
  /**
   * Terminart-Farben (Dark Mode): Kalender-Block und Punkt je Terminart.
   * Im Hellen die Werte aus `constants/meetingTypeStyles`; im Dunkeln tiefe
   * Toene, auf denen die weisse Block-Schrift lesbar bleibt.
   */
  terminart: {
    green: '#2E7D32',
    blue: '#1565C0',
    gold: '#D69D5D',
    purple: '#7B1FA2',
    teal: '#036A8B',
    gray: '#616161',
  },
  /**
   * Google-Event-Farben (`constants/googleEventColors`, colorId 1–11). Im
   * Hellen Googles Werte aus `calendar.colors.get`; im Dunkeln tiefere Toene
   * derselben Farbe (weisse Block-Schrift). Die ID bleibt massgeblich.
   */
  google: {
    g1: '#a4bdfc',
    g2: '#7ae7bf',
    g3: '#dbadff',
    g4: '#ff887c',
    g5: '#fbd75b',
    g6: '#ffb878',
    g7: '#46d6db',
    g8: '#e1e1e1',
    g9: '#5484ed',
    g10: '#51b749',
    g11: '#dc2127',
  },
  /** Teal-Hauch fuer Hover von CTAButton `tertiary`. */
  aktion: {
    tealHauch: 'rgba(3,106,139,0.08)',
  },
} as const satisfies PaletteOptions;

/**
 * Dunkle Palette. Flaechen: Grund #15171E, Karte #1E2029, Hover #262934,
 * Haarlinie #33363F. Schrift: Ueberschrift #F2F2F5 (14,5:1 auf Karte),
 * Fliesstext #B9BBC3 (8,5:1), muted #9C9EA7 (6,1:1). Teal #4FB3D4 (6,7:1),
 * Gold bleibt (7,1:1) mit Navy-Schrift auf Gold-Knoepfen. Navy-Navigation
 * bleibt #272B38, hebt sich vom Grund nur 1,3:1 ab und traegt deshalb die
 * Haarlinie (`navigation.rand`).
 */
export const dunklePalette = {
  mode: 'dark',
  primary: {
    hover: '#16313B',
    // `darker` ist im Bestand eine FLAECHE mit weisser Schrift (Telefonie-Kopf,
    // Kacheln, Avatare) und bleibt deshalb Navy; `dark` ist Hover/Text.
    darker: '#2F3A5C',
    dark: '#6FC3DD',
    main: '#4FB3D4',
    contrastText: '#15171E',
    light: '#16313B',
    altLight: '#7CC4F0',
    altLighter: '#1E3A47',
    metalic: '#2A3550',
    metalicLight: 'rgba(30, 32, 41, 0.88)',
    metalicDark: '#262934',
  },
  background: {
    default: '#15171E',
    paper: '#1E2029',
    screen: '#15171E',
    card: '#262934',
    subtle: '#262934',
    hover: '#2B2E39',
    tooltip: '#3A3D48',
    navigation: '#272B38',
    navigationAktiv: 'rgba(255, 255, 255, 0.08)',
  },
  secondary: {
    main: '#CFA460',
    light: '#E0C590',
    dark: '#E0C590',
    contrastText: '#272B38',
    hoverOnDark: 'rgba(255, 255, 255, 0.06)',
    sky: '#16313B',
    skyLight: '#132A45',
    normalGrey: 'rgba(153, 153, 153, 0.28)',
    darkGrey: '#C4C6CE',
    bluishGrey: '#262934',
  },
  success: {
    light: '#14301D',
    main: '#14301D',
    contrastText: '#81C995',
  },
  error: {
    light: '#3B1D1B',
    main: '#F97066',
    contrastText: '#F28B82',
  },
  warning: {
    main: '#FDB022',
    light: '#FFB74D',
    dark: '#FFB74D',
    contrastText: '#15171E',
    flaeche: '#33290F',
    rahmen: '#6B5416',
    text: '#FDB022',
  },
  info: {
    main: '#7CC4F0',
    light: '#8AB4F8',
    dark: '#7CC4F0',
    contrastText: '#15171E',
    flaeche: '#132A45',
    rahmen: '#21486B',
    text: '#7CC4F0',
  },
  text: {
    primary: '#B9BBC3',
    secondary: '#F2F2F5',
    muted: '#9C9EA7',
    subtle: '#B9BBC3',
    strong: '#F2F2F5',
  },
  grey: {
    50: '#262934',
    100: '#33363F',
    200: '#4A4D57',
    800: '#B9BBC3',
    900: '#F2F2F5',
    A100: '#33363F',
    A200: '#262934',
    A400: '#2F3A5C',
    A700: '#2F3A5C',
  },
  divider: '#33363F',
  status: {
    green: { flaeche: '#14301D', text: '#81C995', punkt: '#81C995' },
    blue: { flaeche: '#132A45', text: '#8AB4F8', punkt: '#8AB4F8' },
    gold: { flaeche: '#33290F', text: '#E6C277', punkt: '#E6C277' },
    orange: { flaeche: '#3A2612', text: '#FFB74D', punkt: '#FFB74D' },
    red: { flaeche: '#3B1D1B', text: '#F28B82', punkt: '#F28B82' },
    gray: { flaeche: '#2A2C33', text: '#C4C6CE', punkt: '#C4C6CE' },
    teal: { flaeche: '#0F2E36', text: '#6FC3DD', punkt: '#6FC3DD' },
    purple: { flaeche: '#2E1D37', text: '#D7A8E0', punkt: '#D7A8E0' },
  },
  chart: {
    petrol: '#3FA9C9',
    gold: '#CFA460',
    goldHell: '#E6C277',
    teal: '#4FB3D4',
    navy: '#8AA0D8',
    blau: '#7CC4F0',
    blauHell: '#4FC3E8',
    gruen: '#66BB6A',
    gruenHell: '#81C784',
    gruenDunkel: '#47CD89',
    orange: '#FF9E40',
    rot: '#EF6B5F',
    gelb: '#F8C80A',
    grau: '#9E9E9E',
    schiefer: '#98A2B3',
    raster: '#33363F',
    achse: '#9C9EA7',
    beschriftung: '#B9BBC3',
    flaeche: '#1E2029',
  },
  navigation: hellePalette.navigation,
  boardSpalte: {
    grau: { akzent: '#98A2B3', flaeche: '#22252D' },
    blau: { akzent: '#4FB3D4', flaeche: '#16313B' },
    gruen: { akzent: '#47CD89', flaeche: '#14301D' },
    gelb: { akzent: '#FDB022', flaeche: '#33290F' },
    rot: { akzent: '#F97066', flaeche: '#3B1D1B' },
    lila: { akzent: '#B692F6', flaeche: '#2A2240' },
  },
  kalender: {
    stundenlinie: 'rgba(255,255,255,0.08)',
    stundenlinieFein: 'rgba(255,255,255,0.05)',
    halbstunde: 'rgba(255,255,255,0.04)',
    kopfRand: 'rgba(255,255,255,0.06)',
    gesperrt: 'rgba(255,255,255,0.03)',
    heute: 'rgba(79,179,212,0.04)',
    heuteHover: 'rgba(79,179,212,0.10)',
    slotHover: 'rgba(255,255,255,0.04)',
    auswahl: 'rgba(79,179,212,0.16)',
    auswahlAktiv: 'rgba(79,179,212,0.26)',
    auswahlRahmen: 'rgba(79,179,212,0.20)',
    abdeckung: 'rgba(255,255,255,0.85)',
    bestaetigt: 'rgba(46,125,50,0.9)',
    bestaetigtHover: 'rgba(46,125,50,1)',
    neutral: 'rgba(120,120,120,0.7)',
    farbRand: 'rgba(255,255,255,0.16)',
  },
  icon: {
    teal: '#4FB3D4',
    tealHell: '#6FC3DD',
    petrol: '#6FC3DD',
    rot: '#F28B82',
    grau: '#9C9EA7',
    schiefer: '#B9BBC3',
    schieferBlau: '#B9BBC3',
  },
  terminart: {
    green: '#2E7D32',
    blue: '#1565C0',
    gold: '#8F6528',
    purple: '#7B1FA2',
    teal: '#036A8B',
    gray: '#616161',
  },
  google: {
    g1: '#4A5FA8',
    g2: '#1F7A5C',
    g3: '#7E4DA8',
    g4: '#B5473D',
    g5: '#7D6510',
    g6: '#A3561A',
    g7: '#17797D',
    g8: '#5C5E66',
    g9: '#3A5DC0',
    g10: '#2F7D2A',
    g11: '#B3261E',
  },
  aktion: {
    tealHauch: 'rgba(79,179,212,0.12)',
  },
} as const satisfies PaletteOptions;

/**
 * CSS-Variable eines Palette-Pfads: `tokenVar('text.secondary')` →
 * `var(--mui-palette-text-secondary)`. Fuer Stellen, die keinen sx-Pfad
 * kennen (Theme-Overrides als Objekt, Typo-Rollen, `style`-Props, SVG). Die
 * Variable folgt dem aktiven Farbschema — im Hellen ist der Wert exakt der
 * alte.
 */
export const tokenVar = (pfad: string): string => `var(--mui-palette-${pfad.split('.').join('-')})`;
